import { Box, Typography } from "@mui/material";

import { CommonDialog } from "@/components/common/CommonDialog";

import { PatientSupportingDocumentRecord } from "./types";

interface PatientSupportingDocumentPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  /** Documento real anexo (dataUrl + mimeType) — sem placeholder. */
  document: PatientSupportingDocumentRecord | null;
}

/**
 * Modal de visualização do arquivo anexado.
 * Usa CommonDialog; data URLs grandes falham com window.open.
 */
export function PatientSupportingDocumentPreviewDialog({
  open,
  onClose,
  document: doc,
}: PatientSupportingDocumentPreviewDialogProps) {
  const mimeType = (doc?.mimeType || "").toLowerCase();
  const dataUrl = doc?.dataUrl ?? "";
  // O Binary relativo é um fallback quando a conversão para data URL falha.
  const previewUrl = dataUrl || doc?.binaryUrl || "";
  // Detecta pelo mimeType salvo OU pelo prefixo do dataUrl do anexo.
  const isImage =
    mimeType.startsWith("image/") || dataUrl.startsWith("data:image/");
  const isPdf =
    mimeType === "application/pdf" ||
    dataUrl.startsWith("data:application/pdf");

  return (
    <CommonDialog
      isOpen={open}
      handleClose={onClose}
      maxWidth="md"
      sheetTitle={doc?.description || doc?.fileName || "Visualizar documento"}
      sheetSubtitle={doc?.description ? doc.fileName : "Documento anexado"}
      sheetSize="full"
    >
      <Box
        sx={{
          width: "100%",
          minHeight: { xs: 280, md: 420 },
          maxHeight: { xs: "70vh", md: "75vh" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
          bgcolor: "grey.100",
          borderRadius: 1,
          mt: 1,
        }}
      >
        {!previewUrl ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum arquivo disponível para visualização.
          </Typography>
        ) : isImage ? (
          // Preview da imagem realmente anexada (dataUrl do registro).
          <Box
            component="img"
            src={previewUrl}
            alt={doc?.fileName || "Documento anexado"}
            sx={{
              maxWidth: "100%",
              maxHeight: { xs: "65vh", md: "70vh" },
              objectFit: "contain",
              display: "block",
            }}
          />
        ) : isPdf ? (
          // Preview do PDF anexado via dataUrl (não abre nova aba).
          <Box
            component="iframe"
            title={doc?.fileName || "PDF anexado"}
            src={previewUrl}
            sx={{
              width: "100%",
              height: { xs: "65vh", md: "70vh" },
              border: 0,
              bgcolor: "background.paper",
            }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary" px={2} textAlign="center">
            Este tipo de arquivo não pode ser pré-visualizado aqui. Baixe ou abra
            pelo nome: {doc?.fileName}
          </Typography>
        )}
      </Box>
    </CommonDialog>
  );
}
